import { zendeskEnvironmentUrl, zendeskRemoteLoginUrl } from '~/config/settings/zendesk';
import { httpRequest } from '~/adapters/httpRequest';

// Implementation details of the Core API
// https://developer.zendesk.com/api-reference/widget-messaging/web/core/
type onLogin = { (callback): void };
type callback = { (): void };
type zE = {
  (target: 'messenger', action: 'show' | 'hide' | 'open' | 'close' | 'logoutUser'): void;
  (target: 'messenger', action: 'loginUser', onLogin: onLogin): void;
  (target: 'messenger:on', action: 'open' | 'close', callback: callback): void;
};

// How to do authentication
// https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/#login

declare global {
  interface Window {
    zE: zE;
  }
}

let loggedIn = false;

// Zendesk Messaging Authentication documentation
// https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/
const loginZendeskUser = () => {
  httpRequest<{ token: string }>('/support/zendesk/getMessagingJWT')
    .then((response) => {
      window.zE('messenger', 'loginUser', (callback) => {
        callback(response.token);
        loggedIn = true;
      });
    })
    .catch(console.error);
};

const logoutZendeskUser = () => {
  if (!loggedIn) return;
  window.zE('messenger', 'logoutUser');
};

/*
 * Zendesk script and key were taken from
 * https://worktangohelp.zendesk.com/admin/channels/messaging_and_social/channels_list
 *
 * On that page, select the Worktango Support (Web Widget) channel.
 */
const attachZendeskWidgetScript = () => {
  // There is no sandbox/dev key. And this is meant to be public.
  const zendeskKey = '9bdfcd73-3faf-4701-ab27-bade432e9647';
  const scriptElement = document.createElement('script');
  scriptElement.type = 'text/javascript';
  scriptElement.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
  scriptElement.async = true;
  scriptElement.id = 'ze-snippet';
  scriptElement.onload = () => {
    window.zE('messenger', 'show');
    loginZendeskUser();
  };

  const firstScriptElement = document.getElementsByTagName('script')[0];
  if (!firstScriptElement?.parentNode) return;
  firstScriptElement.parentNode.insertBefore(scriptElement, firstScriptElement);
};

const startZendeskWidget = () => {
  if (!window.zE) {
    attachZendeskWidgetScript();
  } else {
    window.zE('messenger', 'show');
    loginZendeskUser();
  }
};

const shutdownZendeskWidget = () => {
  const scriptElement = document.getElementById('ze-snippet');
  if (scriptElement) {
    logoutZendeskUser();
    window.zE('messenger', 'hide');
  }
};

const getZendeskURL = (path: string) => {
  return `${zendeskRemoteLoginUrl}?return_to=${zendeskEnvironmentUrl}${path}`;
};

const getZendeskAccessTokenPage = (returnTo: string | null) => {
  const zendeskReturnToParam = returnTo ? `?return_to=${returnTo}` : '';

  return `${zendeskEnvironmentUrl}/access/jwt${zendeskReturnToParam}`;
};

export { startZendeskWidget, shutdownZendeskWidget, getZendeskURL, getZendeskAccessTokenPage };
